import {
    Box,
    Button,
    Checkbox,
    IconButton,
    ListItemText,
    MenuItem,
    MenuList,
    Popper,
    PopperPlacementType,
    Select,
    SelectChangeEvent,
    Typography,
} from "@mui/material"
import React, { useEffect } from "react"
import filter_icon_orange from "../../../assets/images/filter_icon_orange.svg"
import useOutsideElementAlerter from "../../../hooks/useOutsideElementAlerter"

type Props = {
    columns: {
        name: string
        visible: boolean
    }[]
    placement?: PopperPlacementType
    onSubmit: (columns: { name: string; visible: boolean }[]) => void
}

const boxShadow = " -2px -2px 4px 0px #0000000D, 2px 2px 4px 0px #0000000D"

const ColumnSelector = (props: Props) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const [value, setValue] = React.useState<{ name: string; visible: boolean }[]>(props.columns)
    const popperRef = React.useRef<HTMLDivElement>(null)

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget)
    }
    const handleSelectAll = () => {
        const allSelected = value.every(col => col.visible)
        setValue(prev => prev.map(col => ({ ...col, visible: !allSelected })))
    }

    // handle multiple logic
    const handleChange = (event: string) => {
        setValue(prev => prev.map(column => (column.name === event ? { ...column, visible: !column.visible } : column)))
    }

    const handleCancel = () => {
        setAnchorEl(null)
        setValue(props.columns)
    }

    useOutsideElementAlerter(popperRef, handleCancel)

    const handleSubmit = () => {
        props.onSubmit(value)
        setAnchorEl(null)
    }

    const open = Boolean(anchorEl)
    const id = open ? "simple-popper" : undefined
    
    useEffect(() => {
        if(props.columns?.length !== value.length) {
            setValue(props.columns)
        }
    }, [props.columns, value])

    return (
        <>
            <IconButton
                aria-describedby={id}
                sx={{
                    borderRadius: "5px",
                    padding: "8px",
                    width: "32px",
                    height: "32px",
                    border: "1px solid #FC5735",
                }}
                onClick={handleClick}>
                <img src={filter_icon_orange} alt="visible columns" />
            </IconButton>
            <Popper
                id={id}
                open={open}
                anchorEl={anchorEl}
                sx={{ zIndex: 99 }}
                {...(props.placement ? { placement: props.placement } : {})}>
                <Box ref={popperRef} sx={{ bgcolor: "background.paper", boxShadow: boxShadow, borderRadius: "5px" }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "8px 8px",
                            img: {
                                opacity: 0.5,
                            },
                        }}>
                        <img src={filter_icon_orange} height={14} width={14} alt="visible columns" />
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: "12px",
                                fontWeight: "500",
                                color: "statiqOrange.main",
                            }}>
                            Edit Columns
                        </Typography>
                    </Box>
                    <MenuItem sx={{ padding: "4px 6px", borderBottom: "1px solid #eee" }} onClick={handleSelectAll}>
                        <Checkbox
                            sx={{ padding: "1px" }}
                            checked={value.every(col => col.visible)}
                            indeterminate={value.some(col => col.visible) && !value.every(col => col.visible)}
                        />
                        <ListItemText
                            primary={value.every(col => col.visible) ? "Deselect All" : "Select All"}
                            sx={{
                                ".MuiTypography-root": { fontSize: "12px" },
                            }}
                        />
                    </MenuItem>
                    <Box
                        sx={{
                            maxHeight: "55vh",
                            overflowY: "auto",
                        }}>
                        <MenuList sx={{ paddingTop: "0px" }}>
                            {value.map(column => (
                                <MenuItem
                                    sx={{
                                        padding: "4px 6px",
                                    }}
                                    key={column.name}
                                    value={column.name}
                                    onClick={() => handleChange(column.name)}>
                                    <Checkbox
                                        sx={{
                                            padding: "1px",
                                        }}
                                        checked={column.visible}
                                    />
                                    <ListItemText
                                        primary={column.name}
                                        sx={{
                                            ".MuiTypography-root": {
                                                fontSize: "12px",
                                            },
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Box>
                    <Box display={"flex"} gap="12px" alignItems={"center"} justifyContent={"center"} p={1}>
                        <Button variant="light" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button variant="contained" color="statiqOrange" onClick={handleSubmit}>
                            Save
                        </Button>
                    </Box>
                </Box>
            </Popper>
        </>
    )
}

export default ColumnSelector
